// ✅ src/pages/TarMixing/ProductionOutward.jsx
// Updated with safe localStorage handling + common headers

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProductionLayoutWrapper from "../../components/ProductionLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPaperPlane,FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const toNum = (v, def = 0) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : def;
};

const toStr = (v, def = "") => {
  const s = String(v ?? "").trim();
  return s || def;
};

const isoDate = (d = new Date()) => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const ProductionOutward = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ Safe localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchid = useMemo(() => toInt(branchIdLS, 8), [branchIdLS]);
  const branchtype = useMemo(() => toStr(branchTypeLS, "HO"), [branchTypeLS]);
  const userid = useMemo(() => toStr(userIdLS, ""), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchid,
      "x-branch": branchtype,
      "x-branchtype": branchtype,
      "x-userid": userid,
    }),
    [finYearId, finYear, branchid, branchtype, userid]
  );

  const [loading, setLoading] = useState(false);
  const [loadingProductions, setLoadingProductions] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // dropdowns
  const [productionOptions, setProductionOptions] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    outwardNo: "",
    productionid: "",
    productionName: "",
    productionCode: "",
    transferredQty: "0",
    date: isoDate(new Date()),
    projectId: "",
    projectName: "",
    productionQty: "0",
    balanceQty: "0",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetProductionDetails = () => {
    setFormData((p) => ({
      ...p,
      projectId: "",
      projectName: "",
      productionCode: "",
      productionQty: "0",
      balanceQty: "0",
      transferredQty: "0",
    }));
  };

  // -----------------------------
  // API Calls
  // -----------------------------
  const fetchOutwardNo = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/outwardEntry/generateOutwardNo`, {
        params: { branchid, userid, branchtype },
        headers: commonHeaders,
      });

      const outwardNo = res.data?.data?.outwardNo || "";
      setFormData((p) => ({ ...p, outwardNo }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to generate Outward No");
    }
  };

  const fetchProductions = async () => {
    try {
      setLoadingProductions(true);

      const res = await axios.get(`${BASE_URL}/api/outwardEntry/productions`, {
        params: { branchid, userid, branchtype },
        headers: commonHeaders,
      });

      setProductionOptions(res.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load productions");
      setProductionOptions([]);
    } finally {
      setLoadingProductions(false);
    }
  };

  const fetchProductionDetails = async (productionid) => {
    try {
      if (!productionid) {
        resetProductionDetails();
        return;
      }

      setLoadingDetails(true);

      const res = await axios.get(`${BASE_URL}/api/outwardEntry/productionDetails`, {
        params: { branchid, userid, branchtype, productionid },
        headers: commonHeaders,
      });

      const d = res.data?.data;
      if (!d) {
        toast.info("No details found for this production");
        resetProductionDetails();
        return;
      }

      setFormData((p) => ({
        ...p,
        productionid: d.productionId ?? productionid,
        productionName: d.productionName ?? p.productionName,
        projectId: d.projectId ?? "",
        projectName: d.projectName ?? "",
        productionCode: d.productionCode ?? "",
        productionQty: String(d.productionQty ?? 0),
        transferredQty: String(d.transferedQty ?? 0),
        balanceQty: String(d.balanceQty ?? 0),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to fetch production details");
    } finally {
      setLoadingDetails(false);
    }
  };

  // -----------------------------
  // Effects
  // -----------------------------
  useEffect(() => {
    fetchOutwardNo();
    fetchProductions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchid, userid, branchtype, commonHeaders]);

  useEffect(() => {
    if (formData.productionid) {
      fetchProductionDetails(formData.productionid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.productionid]);

  // -----------------------------
  // Submit
  // -----------------------------
  const handleSubmit = async () => {
    if (!formData.productionid) {
      return toast.error("Production Name is required");
    }
    if (!formData.date) {
      return toast.error("Date is required");
    }

    const transferredQty = toNum(formData.transferredQty);
    const balanceQty = toNum(formData.balanceQty);
    const productionQty = toNum(formData.productionQty);

    if (transferredQty <= 0) {
      return toast.error("Transferred Qty must be greater than 0");
    }

    if (balanceQty > 0 && transferredQty > balanceQty) {
      return toast.error("Transferred Qty cannot be greater than Balance Qty");
    }

    try {
      setLoading(true);

      const payload = {
        branchid,
        userid,
        branchtype,
        date: formData.date,
        outwardNo: formData.outwardNo,

        productionid: formData.productionid,
        productionName: formData.productionName,

        projectId: formData.projectId || null,
        projectName: formData.projectName || null,

        productionCode: formData.productionCode || null,
        productionQty,
        transferredQty,
        balanceQty: Math.max(balanceQty - transferredQty, 0),
      };

      const res = await axios.post(`${BASE_URL}/api/outwardEntry/save`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success(
          `Saved! Outward No: ${res.data?.data?.outwardNo || formData.outwardNo}`
        );

        setFormData({
          outwardNo: "",
          productionid: "",
          productionName: "",
          productionCode: "",
          transferredQty: "0",
          date: isoDate(new Date()),
          projectId: "",
          projectName: "",
          productionQty: "0",
          balanceQty: "0",
        });

        await fetchOutwardNo();
        await fetchProductions();

        // optional redirect:
        // navigate("/TarMixing/Tar_Outward_Listing");
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Server error while saving");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProductionLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PRODUCTION OUTWARD</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <button
              type="button"
              onClick={() => navigate("/TarMixing/Tar_Outward_Listing")}
               className={`inline-flex items-center justify-center gap-2 whitespace-nowrap px-8 py-3 rounded-lg font-semibold border ${
    isDark
      ? "border-gray-600 text-white hover:bg-gray-700"
      : "border-gray-300 text-gray-800 hover:bg-gray-100"
  }`}
            >
             <FaArrowLeft />Back
            </button>

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >

          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Production Outward
            </h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Left */}
            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Outward No
                </label>
                <input
                  type="text"
                  value={formData.outwardNo}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                  readOnly
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Production Name <span className="text-red-500">*</span>
                </label>

                <select
                  value={formData.productionid}
                  onChange={(e) => {
                    const pid = e.target.value;
                    const selected = productionOptions.find(
                      (x) => String(x.productionId ?? x.id) === String(pid)
                    );
                    handleInputChange("productionid", pid);
                    handleInputChange(
                      "productionName",
                      selected?.productionName || selected?.name || ""
                    );
                  }}
                  disabled={loadingProductions || loading}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">
                    {loadingProductions ? "Loading..." : "--Select--"}
                  </option>
                  {productionOptions.map((p, idx) => (
                    <option
                      key={p.productionId ?? p.id ?? idx}
                      value={p.productionId ?? p.id ?? ""}
                    >
                      {p.productionName ?? p.name ?? ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Production Code
                </label>
                <input
                  type="text"
                  value={formData.productionCode}
                  onChange={(e) => handleInputChange("productionCode", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                  placeholder="Production code"
                  readOnly
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Transferred Qty <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.transferredQty}
                  onChange={(e) => handleInputChange("transferredQty", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                  min="0"
                  step="0.001"
                />
              </div>
            </div>

            {/* Right */}
            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Project Name
                </label>
                <input
                  type="text"
                  value={formData.projectName}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                  readOnly
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Production Qty
                </label>
                <input
                  type="number"
                  value={formData.productionQty}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                  readOnly
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Balance Qty
                </label>
                <input
                  type="number"
                  value={formData.balanceQty}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                  readOnly
                />
              </div>
            </div>
          </div>

          {loadingDetails && (
            <div className={`mb-6 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Loading production details...
            </div>
          )}

          <div className="flex justify-end gap-4">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              <FaPaperPlane />
              {loading ? "Saving..." : "Submit"}
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProductionLayoutWrapper>
  );
};

export default ProductionOutward;